import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { useFactures, useTableauDeBord } from '@/api/hooks';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, PageHeader, Vide } from '@/components/common';
import { StatutFactureBadge } from '@/components/courrier';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn, dateFr, montant } from '@/lib/utils';

type Vue = 'tous' | 'parapheur' | 'a-comptabiliser' | 'a-engager';

export function FactureListe() {
  const session = useSession();
  const u = session!.utilisateur;
  const tdb = useTableauDeBord();
  const assistante = u.profils.includes('ASSISTANT') || u.profils.includes('ADMINISTRATEUR');
  const comptable = u.profils.includes('CHEF_COMPTABLE');
  const parapheur = Boolean(tdb.data?.courrier.parapheur);
  const [vue, setVue] = useState<Vue>(comptable ? 'a-comptabiliser' : 'tous');
  const [q, setQ] = useState('');
  const { data, isLoading, error } = useFactures({ vue, q: q.trim() || undefined });

  return (
    <div>
      <PageHeader
        titre="Factures reçues"
        sousTitre="Enregistrement, orientation par le Directeur Général, comptabilisation, engagement (FED) et paiement sous 30 jours."
        actions={
          assistante && (
            <Button asChild>
              <Link to="/factures/nouvelle">
                <Plus className="size-4" /> Enregistrer une facture
              </Link>
            </Button>
          )
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Tabs value={vue} onValueChange={(v) => setVue(v as Vue)}>
          <TabsList>
            {parapheur && <TabsTrigger value="parapheur">Parapheur</TabsTrigger>}
            <TabsTrigger value="a-comptabiliser">À comptabiliser</TabsTrigger>
            <TabsTrigger value="a-engager">À engager (CPM)</TabsTrigger>
            <TabsTrigger value="tous">Toutes</TabsTrigger>
          </TabsList>
        </Tabs>
        <Input className="w-64" placeholder="Numéro, fournisseur, objet" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {isLoading ? (
        <Chargement />
      ) : error ? (
        <ErreurBloc erreur={error} />
      ) : !data?.length ? (
        <Vide message="Aucune facture." />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Numéro</TableHead>
                <TableHead>Fournisseur</TableHead>
                <TableHead className="hidden md:table-cell">Objet</TableHead>
                <TableHead className="text-right">Montant</TableHead>
                <TableHead className="hidden sm:table-cell">Reçue le</TableHead>
                <TableHead className="hidden lg:table-cell">Commande</TableHead>
                <TableHead>Statut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((f) => (
                <TableRow key={f.id}>
                  <TableCell>
                    <Link to={`/factures/${f.id}`} className="font-medium text-primary hover:underline">
                      {f.numero}
                    </Link>
                    <div className="text-xs text-muted-foreground">{f.numeroFacture}</div>
                  </TableCell>
                  <TableCell className="max-w-[22ch] truncate">{f.fournisseur}</TableCell>
                  <TableCell className="hidden max-w-[32ch] truncate md:table-cell">{f.objet}</TableCell>
                  <TableCell className="num text-right">
                    {montant(f.montantUsd, true)}
                    {f.devise !== 'USD' && <div className="text-xs text-muted-foreground">{f.montant.toLocaleString('fr-FR')} {f.devise}</div>}
                  </TableCell>
                  <TableCell className={cn('hidden sm:table-cell', f.delaiDepasse && 'font-semibold text-red-700', f.procheDelai && !f.delaiDepasse && 'text-amber-700')}>
                    {dateFr(f.dateReception)}
                    <div className="text-xs">{f.jours} j{f.delaiDepasse ? ' · délai dépassé' : f.procheDelai ? ' · proche du délai' : ''}</div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {f.bonCommande ? (
                      <Link to={`/achats/${f.bonCommande.id}`} className="text-primary hover:underline">
                        {f.bonCommande.numero ?? 'BC'}
                      </Link>
                    ) : (
                      (f.referenceCommande ?? '—')
                    )}
                    {f.ecartCommande !== null && f.ecartCommande !== 0 && <div className={cn('text-xs', f.ecartCommande > 0 ? 'text-red-700' : 'text-muted-foreground')}>écart {montant(f.ecartCommande, true)}</div>}
                  </TableCell>
                  <TableCell>
                    <StatutFactureBadge statut={f.statut} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
